import { Injectable, signal, computed } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly STORAGE_KEY = 'IRMA_SETTINGS';
  private readonly settingsSignal = signal<Record<string, any>>({});
  readonly settings = computed(() => this.settingsSignal());

  constructor() {
    this.loadSettings();
  }

  get<T = any>(key: string): T | null {
    return this.settingsSignal()[key] ?? null;
  }

  getSetting<T = any>(key: string) {
    return computed<T | null>(() => this.settingsSignal()[key] ?? null);
  }

  set<T = any>(key: string, value: T): void {
    this.settingsSignal.update(current => ({
      ...current,
      [key]: value
    }));
    this.persist();
  }

  remove(key: string): void {
    this.settingsSignal.update(current => {
      const { [key]: _, ...rest } = current;
      return rest;
    });
    this.persist();
  }

  clear(): void {
    this.settingsSignal.set({});
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('[SettingsService] Failed to clear storage:', error);
    }
  }

  update(updaterFn: (current: Record<string, any>) => Record<string, any>): void {
    this.settingsSignal.update(updaterFn);
    this.persist();
}

  private loadSettings(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.settingsSignal.set(parsed);
    }
    } catch (error) {
      console.error('[SettingsService] Failed to load settings:', error);
      this.settingsSignal.set({});
    }
  }

  private persist(): void {
    try {
      const current = this.settingsSignal();
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
    } catch (error) {
      console.error('[SettingsService] Failed to persist settings:', error);
    }
  }
}
